'use client';

import {
	Alert,
	Box,
	Button,
	Grid,
	LinearProgress,
	MenuItem,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { gql, useQuery } from '@parkchirp/api-client';
import {
	AccessSystemParkerActionDisplayEnum,
	AccessSystemParkerStatusDisplayEnum,
	type AccessSystemParkerStatusEnum,
} from 'parkchirp-shared/enums';
import React, { useEffect, useState } from 'react';
import { dayjs } from '../../../utils/dayjs';

const GET_PARKER_ACCESS_HISTORY = gql`
	query getParkerAccessHistory($parkerId: String!, $tenantSubscriptionId: String, $offset: Int, $limit: Int, $status: AccessSystemParkerStatusEnum) {
		getParkerAccessHistory(parkerId: $parkerId, tenantSubscriptionId: $tenantSubscriptionId, offset: $offset, limit: $limit, status: $status) {
			total timezone
			rows { id createdAt action jobStatus user }
		}
	}
`;

type HistoryRow = {
	id: number;
	createdAt: string;
	action: string;
	jobStatus: string;
	user: string;
};

function getActionLabel(row: HistoryRow): string {
	if (row.action === 'SET_PARKER_TO_NEUTRAL') return 'Set Parker to Neutral';
	return AccessSystemParkerActionDisplayEnum[row.action] || row.action;
}

type ParkerAccessHistoryProps = {
	parkerId: string;
	tenantSubscriptionId?: string;
	refreshKey: number;
};

export function ParkerAccessHistory({
	facilityIntegration,
	...props
}: ParkerAccessHistoryProps & { facilityIntegration: string | null | undefined }) {
	if (!facilityIntegration || facilityIntegration === 'NONE') return null;
	return <ParkerAccessHistoryContent {...props} />;
}

function ParkerAccessHistoryContent({ parkerId, tenantSubscriptionId, refreshKey }: ParkerAccessHistoryProps) {
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [status, setStatus] = useState<AccessSystemParkerStatusEnum | 'ALL'>('ALL');
	const { data, previousData, loading, error, refetch, startPolling, stopPolling } = useQuery<{
		getParkerAccessHistory: { rows: HistoryRow[]; total: number; timezone: string };
	}>(GET_PARKER_ACCESS_HISTORY, {
		variables: {
			parkerId,
			tenantSubscriptionId,
			offset: page * rowsPerPage,
			limit: rowsPerPage,
			status: status === 'ALL' ? null : status,
		},
		context: { apiVersion: 'v2', appName: process.env.NEXT_PUBLIC_APP_NAME },
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
	});
	useEffect(() => {
		if (refreshKey) refetch().catch(() => undefined);
	}, [refreshKey, refetch]);
	const history = data?.getParkerAccessHistory ?? previousData?.getParkerAccessHistory;
	// Old jobs can remain pending after a sync archives them; don't poll those indefinitely.
	const pending = history?.rows.some((row) => {
		const age = Date.now() - new Date(row.createdAt).getTime();
		return (row.jobStatus === 'NEW' || row.jobStatus === 'PENDING') && age >= 0 && age < 10 * 60 * 1000;
	});
	useEffect(() => {
		if (pending) startPolling(10000);
		else stopPolling();
		return stopPolling;
	}, [pending, startPolling, stopPolling]);
	if (!loading && !history?.rows.length && status === 'ALL') {
		// Keep failures retryable without showing an empty history section.
		return error ? (
			<Grid item xs={12}>
				<Alert severity='error' action={<Button onClick={() => refetch()}>Retry</Button>}>
					Unable to load parker access history.
				</Alert>
			</Grid>
		) : null;
	}
	return (
		<Grid item xs={12} sx={{ mb: 3 }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: 'wrap',
					gap: 2,
				}}
			>
				<h2>Parker Access History</h2>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<TextField
						select
						id='parker-access-history-status'
						label='Status'
						size='small'
						value={status}
						onChange={(event) => {
							setStatus(event.target.value as AccessSystemParkerStatusEnum | 'ALL');
							setPage(0);
						}}
						sx={{ minWidth: 160 }}
					>
						<MenuItem value='ALL'>All statuses</MenuItem>
						{Object.entries(AccessSystemParkerStatusDisplayEnum).map(([value, label]) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</TextField>
					<Button onClick={() => refetch()} disabled={loading}>
						Refresh
					</Button>
				</Box>
			</Box>
			<Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
				Times shown in {history?.timezone || 'the facility timezone'}. Status shows the integration result.
			</Typography>
			{loading && <LinearProgress aria-label='Loading parker access history' />}
			{error ? (
				<Alert severity='error'>Unable to load parker access history. Use Refresh to try again.</Alert>
			) : (
				<>
					<TableContainer>
						<Table size='small' aria-label='Parker Access History'>
							<TableHead>
								<TableRow>
									{['Date/Time', 'Status', 'Action', 'User'].map((label) => (
										<TableCell key={label}>{label}</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{loading &&
									!history?.rows.length &&
									[0, 1, 2].map((rowIndex) => (
										<TableRow key={rowIndex}>
											{[0, 1, 2, 3].map((columnIndex) => (
												<TableCell key={columnIndex}>
													<Skeleton />
												</TableCell>
											))}
										</TableRow>
									))}
								{!loading && !history?.rows.length && (
									<TableRow>
										<TableCell colSpan={4} align='center'>
											No access history matches this status.
										</TableCell>
									</TableRow>
								)}
								{history?.rows.map((row) => (
									<TableRow key={row.id}>
										<TableCell sx={{ whiteSpace: 'nowrap' }}>
											{dayjs(row.createdAt).tz(history.timezone).format('MM/DD/YYYY h:mm A z')}
										</TableCell>
										<TableCell>
											<Typography
												variant='body2'
												component='span'
												color={row.jobStatus === 'FAILED' ? 'error' : 'text.primary'}
											>
												{AccessSystemParkerStatusDisplayEnum[row.jobStatus] || row.jobStatus}
											</Typography>
										</TableCell>
										<TableCell>{getActionLabel(row)}</TableCell>
										<TableCell>{row.user}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						component='div'
						count={history?.total ?? previousData?.getParkerAccessHistory.total ?? 0}
						page={page}
						rowsPerPage={rowsPerPage}
						rowsPerPageOptions={[10, 25, 50]}
						onPageChange={(_, value) => setPage(value)}
						onRowsPerPageChange={(event) => {
							setRowsPerPage(Number(event.target.value));
							setPage(0);
						}}
					/>
				</>
			)}
		</Grid>
	);
}
